/**
 * WhatsApp Connection Manager
 *
 * Gestisce tutti gli stati della connessione WhatsApp:
 * - STOPPED: Mostra pulsante per attivare
 * - STARTING: Mostra loader
 * - SCAN_QR_CODE: Mostra QR code e polling
 * - WORKING: Nascosto (passa alle chat)
 * - FAILED: Mostra errore e retry
 */

import React, { useState, useEffect } from 'react';
import { Loader, Check, AlertCircle, RefreshCw, MessageCircle } from 'lucide-react';
import { useApp } from '../AppProvider';
import {
  type SessionStatus
} from '../../lib/utils/whatsapp-setup';
import {
  getWahaSessionStatus,
  getWahaQRCode,
  createWahaSession,
  startWahaSession,
} from '../../lib/services/wahaProxyService';

interface WhatsAppConnectionManagerProps {
  wahaUrl: string;
  wahaApiKey: string;
  sessionName: string;
  configId?: string;
  onConnected: () => void;
  onReconfigureNeeded?: () => void;
}

export function WhatsAppConnectionManager({
  wahaUrl,
  wahaApiKey,
  sessionName,
  configId,
  onConnected,
  onReconfigureNeeded
}: WhatsAppConnectionManagerProps) {
  const { showToast } = useApp();

  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('STOPPED');
  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Initial status check
  useEffect(() => {
    checkStatus();
  }, []);

  /**
   * Controlla lo stato della sessione
   */
  const checkStatus = async () => {
    try {
      setLoading(true);
      const status = await getWahaSessionStatus(sessionName) as SessionStatus;
      setSessionStatus(status);

      if (status === 'WORKING') {
        // Già connesso!
        onConnected();
      } else if (status === 'SCAN_QR_CODE') {
        // Recupera QR code esistente
        try {
          const qr = await getWahaQRCode(sessionName);
          setQrCode(qr);
          startPolling();
        } catch (error) {
          console.error('[ConnectionManager] Errore recupero QR:', error);
        }
      } else if (status === 'NOT_FOUND') {
        // Sessione non trovata sul server WAHA
        console.warn('[ConnectionManager] Sessione non trovata - riconfigurazione necessaria');
      }
    } catch (error) {
      console.error('[ConnectionManager] Errore check status:', error);
      setSessionStatus('FAILED');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Avvia la sessione
   */
  const handleActivate = async () => {
    setActionLoading(true);

    try {
      // Controlla se la sessione esiste, se no la crea
      const existsStatus = await getWahaSessionStatus(sessionName);
      if (existsStatus === 'NOT_FOUND' && configId) {
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        await createWahaSession(sessionName, configId, supabaseAnonKey);
      }
      await startWahaSession(sessionName);
      await new Promise(resolve => setTimeout(resolve, 2000));
      const qr = await getWahaQRCode(sessionName);
      setQrCode(qr);
      setSessionStatus('SCAN_QR_CODE');
      startPolling();
      showToast?.('Sessione avviata! Scansiona il QR code', 'success');
    } catch (error) {
      console.error('[ConnectionManager] Errore attivazione:', error);
      showToast?.('Errore durante l\'attivazione', 'error');
      setSessionStatus('FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Rigenera QR code
   */
  const handleRefreshQR = async () => {
    setActionLoading(true);

    try {
      const qr = await getWahaQRCode(sessionName);
      setQrCode(qr);
      showToast?.('QR code rigenerato!', 'success');
    } catch (error) {
      console.error('[ConnectionManager] Errore rigenerazione QR:', error);
      showToast?.('Errore durante la rigenerazione del QR code', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Avvia polling status
   */
  const startPolling = () => {
    // Ferma polling precedente se esiste
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Polling ogni 3 secondi
    const interval = setInterval(async () => {
      try {
        const status = await getWahaSessionStatus(sessionName) as SessionStatus;
        setSessionStatus(status);

        if (status === 'WORKING') {
          // Success! Ferma polling
          clearInterval(interval);
          setPollingInterval(null);
          showToast?.('WhatsApp connesso! 🎉', 'success');
          onConnected();
        }

        if (status === 'FAILED') {
          // Errore! Ferma polling
          clearInterval(interval);
          setPollingInterval(null);
          showToast?.('Connessione fallita', 'error');
        }

        if (status === 'NOT_FOUND') {
          // Sessione non trovata! Ferma polling
          clearInterval(interval);
          setPollingInterval(null);
          showToast?.('Sessione WhatsApp non trovata. Riconfigurazione necessaria.', 'error');
        }
      } catch (error) {
        console.error('[ConnectionManager] Errore polling:', error);
      }
    }, 3000);

    setPollingInterval(interval);
  };

  /**
   * Render: Loading iniziale (Notion-style)
   */
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#FBFBFA] dark:bg-gray-950">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#25D366] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#787774]">Controllo stato connessione...</p>
        </div>
      </div>
    );
  }

  /**
   * Render: STOPPED - Attivazione richiesta (Notion-style)
   */
  if (sessionStatus === 'STOPPED') {
    return (
      <div className="h-full overflow-y-auto bg-[#FBFBFA] dark:bg-gray-950">
        <div className="max-w-2xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mx-auto mb-6 shadow-lg">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#37352F] dark:text-white mb-3">
              Connetti WhatsApp
            </h1>
            <p className="text-base text-[#787774] max-w-md mx-auto">
              Il tuo account è configurato. Ora collega il tuo telefono per iniziare a chattare.
            </p>
          </div>

          {/* Steps */}
          <div className="grid gap-4 mb-10">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold">
                1
              </div>
              <div>
                <h3 className="font-medium text-[#37352F] dark:text-white mb-1">Premi "Attiva WhatsApp"</h3>
                <p className="text-sm text-[#787774]">Clicca il pulsante qui sotto per generare un codice QR univoco.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold">
                2
              </div>
              <div>
                <h3 className="font-medium text-[#37352F] dark:text-white mb-1">Apri WhatsApp sul telefono</h3>
                <p className="text-sm text-[#787774]">Vai su Menu → Dispositivi collegati → Collega un dispositivo.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold">
                3
              </div>
              <div>
                <h3 className="font-medium text-[#37352F] dark:text-white mb-1">Scansiona il QR code</h3>
                <p className="text-sm text-[#787774]">Inquadra il codice QR con la fotocamera e sei connesso!</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={handleActivate}
              disabled={actionLoading}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-medium text-lg rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {actionLoading ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  Attivazione in corso...
                </>
              ) : (
                <>
                  <MessageCircle className="w-6 h-6" />
                  Attiva WhatsApp
                </>
              )}
            </button>
            <p className="text-xs text-[#787774] mt-4">
              Il QR code scade dopo qualche minuto, puoi rigenerarlo se necessario
            </p>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render: STARTING - In avvio (Notion-style)
   */
  if (sessionStatus === 'STARTING') {
    return (
      <div className="h-full flex items-center justify-center bg-[#FBFBFA] dark:bg-gray-950">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Loader className="w-8 h-8 text-white animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-[#37352F] dark:text-white mb-2">
            Avvio in corso...
          </h2>
          <p className="text-[#787774]">Preparazione della sessione WhatsApp</p>
        </div>
      </div>
    );
  }

  /**
   * Render: SCAN_QR_CODE - Mostra QR (Notion-style)
   */
  if (sessionStatus === 'SCAN_QR_CODE') {
    return (
      <div className="h-full overflow-y-auto bg-[#FBFBFA] dark:bg-gray-950">
        <div className="max-w-2xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#37352F] dark:text-white mb-3">
              Scansiona il QR Code
            </h1>
            <p className="text-base text-[#787774]">
              Inquadra il codice con WhatsApp per collegare il tuo account
            </p>
          </div>

          {/* QR Code */}
          {qrCode && (
            <div className="flex justify-center mb-8">
              <div className="p-6 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-[rgba(55,53,47,0.09)] dark:border-white/10">
                <img
                  src={`data:image/png;base64,${qrCode}`}
                  alt="QR Code WhatsApp"
                  className="w-64 h-64 sm:w-72 sm:h-72"
                />
              </div>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center justify-center gap-3 mb-8 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
            <Loader className="w-5 h-5 text-[#2383E2] animate-spin" />
            <span className="text-[#2383E2] font-medium">In attesa di scansione...</span>
          </div>

          {/* Instructions */}
          <div className="grid gap-3 mb-8">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold text-sm">
                1
              </div>
              <p className="text-sm text-[#37352F] dark:text-white">Apri WhatsApp sul telefono</p>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold text-sm">
                2
              </div>
              <p className="text-sm text-[#37352F] dark:text-white">Menu → Dispositivi collegati → Collega</p>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold text-sm">
                3
              </div>
              <p className="text-sm text-[#37352F] dark:text-white">Inquadra questo QR code</p>
            </div>
          </div>

          {/* Refresh button */}
          <div className="text-center">
            <button
              onClick={handleRefreshQR}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-gray-900 text-[#37352F] dark:text-white border border-[rgba(55,53,47,0.16)] dark:border-white/20 rounded-lg hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
              Rigenera QR Code
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render: NOT_FOUND - Sessione scaduta (Notion-style)
   */
  if (sessionStatus === 'NOT_FOUND') {
    return (
      <div className="h-full flex items-center justify-center bg-[#FBFBFA] dark:bg-gray-950">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>

          <h2 className="text-xl font-semibold text-[#37352F] dark:text-white mb-3">
            Sessione Scaduta
          </h2>

          <p className="text-[#787774] mb-8">
            La sessione WhatsApp non è più disponibile. Riconfigura la connessione per continuare.
          </p>

          <button
            onClick={() => {
              if (onReconfigureNeeded) {
                onReconfigureNeeded();
              } else {
                window.location.reload();
              }
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-medium rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            Riconfigura WhatsApp
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render: FAILED - Errore (Notion-style)
   */
  if (sessionStatus === 'FAILED') {
    return (
      <div className="h-full flex items-center justify-center bg-[#FBFBFA] dark:bg-gray-950">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>

          <h2 className="text-xl font-semibold text-[#37352F] dark:text-white mb-3">
            Connessione Fallita
          </h2>

          <p className="text-[#787774] mb-8">
            La sessione WhatsApp è stata disconnessa. Riconfigura la connessione per continuare.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                if (onReconfigureNeeded) {
                  onReconfigureNeeded();
                } else {
                  window.location.reload();
                }
              }}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-medium rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Riconfigura WhatsApp
            </button>

            <button
              onClick={handleActivate}
              disabled={actionLoading}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-gray-900 text-[#37352F] dark:text-white border border-[rgba(55,53,47,0.16)] dark:border-white/20 rounded-xl hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Riprovo...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Riprova connessione esistente
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default: WORKING (non dovrebbe mai arrivare qui)
  return null;
}
